import React, { useState } from 'react'
import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@material-ui/core'
import EditOutlined from '@material-ui/icons/EditOutlined'
import DeleteOutline from '@material-ui/icons/DeleteOutline'
import AddIcon from '@material-ui/icons/Add'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { deleteTenantUser, TenantUserInfo } from '../../redux/actions/dashboard'
import SetPasswordDialog from './SetPasswordDialog'
import TenantUserDialog from './TenantUserDialog'

/*
 * Tabulka loginov (TenantUser) jedneho tenanta: pridat, premenovat, nastavit heslo, zmazat.
 * Pouziva superadmin (TenantOverview - kazdy tenant) aj bezny admin (OwnTenantUsers - svoj tenant).
 * Vlastny login: nastavenie hesla len superadmin (ostatni cez "Change password"), zmazat nikto.
 */

const useStyles = makeStyles((theme) => ({
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 32,
        marginBottom: theme.spacing(0.5),
    },
    title: {
        fontWeight: 600,
    },
    table: {
        '& td, & th': {
            paddingTop: theme.spacing(0.75),
            paddingBottom: theme.spacing(0.75),
        },
    },
    actionsCell: {
        width: 1,
        whiteSpace: 'nowrap',
    },
    you: {
        marginLeft: theme.spacing(1),
    },
    empty: {
        color: theme.palette.text.secondary,
        fontStyle: 'italic',
    },
}))

interface Props {
    users: TenantUserInfo[]
    tenantId?: string
    tenantName?: string
    isSuperAdmin: boolean
    loadError?: string
    // zobrazit nadpis "Tenant users" (v akordeone ano, v samostatnej sekcii ho ma rodic)
    showTitle?: boolean
    onChanged: () => void
    onSuccess: (msg: string) => void
    onError: (msg: string) => void
}

export default function TenantUsersTable({
    users,
    tenantId,
    tenantName,
    isSuperAdmin,
    loadError,
    showTitle = true,
    onChanged,
    onSuccess,
    onError,
}: Props) {
    const classes = useStyles()
    const { t } = useTranslation()
    const currentUserId: string | undefined = useSelector((state: any) => state.login.value.user.id)

    const [passwordUser, setPasswordUser] = useState<TenantUserInfo>()
    const [userDialog, setUserDialog] = useState<{ user?: TenantUserInfo }>()
    const [userToDelete, setUserToDelete] = useState<TenantUserInfo>()
    const [deleting, setDeleting] = useState(false)

    const isMe = (user: TenantUserInfo) => Boolean(currentUserId && user.id.toLowerCase() === currentUserId.toLowerCase())

    const handleDelete = async () => {
        if (!userToDelete) return
        setDeleting(true)
        try {
            await deleteTenantUser(userToDelete.id)
            onSuccess(t('dashboard.loginDeleted'))
            setUserToDelete(undefined)
            onChanged()
        } catch {
            onError(t('dashboard.errorDeletingLogin'))
        } finally {
            setDeleting(false)
        }
    }

    return (
        <div>
            <div className={classes.header}>
                {showTitle ? (
                    <Typography variant="subtitle2" className={classes.title}>
                        {t('dashboard.tenantUsersTitle')}
                    </Typography>
                ) : (
                    <span />
                )}
                {loadError ? null : (
                    <Button
                        size="small"
                        color="primary"
                        startIcon={<AddIcon fontSize="small" />}
                        onClick={() => setUserDialog({})}
                    >
                        {t('dashboard.addLogin')}
                    </Button>
                )}
            </div>
            <Table size="small" className={classes.table}>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('dashboard.login')}</TableCell>
                        <TableCell className={classes.actionsCell} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loadError ? (
                        <TableRow>
                            <TableCell colSpan={2} className={classes.empty}>
                                {t('dashboard.errorLoadingTenantUsers')} ({loadError})
                            </TableCell>
                        </TableRow>
                    ) : users.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={2} className={classes.empty}>
                                {t('dashboard.noLogins')}
                            </TableCell>
                        </TableRow>
                    ) : (
                        users.map((user) => {
                            const me = isMe(user)
                            return (
                                <TableRow key={user.id} hover>
                                    <TableCell>
                                        {user.login}
                                        {me ? <Chip size="small" label={t('dashboard.you')} className={classes.you} /> : null}
                                    </TableCell>
                                    <TableCell className={classes.actionsCell}>
                                        {!me || isSuperAdmin ? (
                                            <Button
                                                size="small"
                                                color="primary"
                                                onClick={() => setPasswordUser({ ...user, tenantName })}
                                            >
                                                {t('dashboard.setPassword')}
                                            </Button>
                                        ) : null}
                                        <Tooltip title={t('dashboard.editLogin') as string}>
                                            <IconButton size="small" onClick={() => setUserDialog({ user })}>
                                                <EditOutlined fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {/* vlastny login sa zmazat neda (backend vrati 403) */}
                                        <Tooltip title={t('dashboard.deleteLogin') as string}>
                                            <span>
                                                <IconButton size="small" disabled={me} onClick={() => setUserToDelete(user)}>
                                                    <DeleteOutline fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )
                        })
                    )}
                </TableBody>
            </Table>

            <SetPasswordDialog
                user={passwordUser}
                onClose={() => setPasswordUser(undefined)}
                onSuccess={onSuccess}
                onError={onError}
            />

            <TenantUserDialog
                open={Boolean(userDialog)}
                user={userDialog?.user}
                tenantId={tenantId}
                tenantName={tenantName}
                onClose={() => setUserDialog(undefined)}
                onSaved={() => {
                    setUserDialog(undefined)
                    onSuccess(t('dashboard.loginSaved'))
                    onChanged()
                }}
                onError={onError}
            />

            <Dialog open={Boolean(userToDelete)} onClose={() => !deleting && setUserToDelete(undefined)}>
                <DialogTitle>{t('dashboard.deleteLogin')}</DialogTitle>
                <DialogContent>
                    <Typography>{t('dashboard.deleteLoginConfirm', { login: userToDelete?.login })}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUserToDelete(undefined)} disabled={deleting}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="contained" color="secondary" onClick={handleDelete} disabled={deleting}>
                        {deleting ? <CircularProgress size={20} /> : t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
